"use client";

import { useSearchParams, useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { generateErrorMessage } from "@/lib/errors";
import type { ErrorResponse } from "@/lib/errors";

import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const formSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export default function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      const callbackPath = searchParams.get("callbackPath") || "/";
      const url = new URL(callbackPath, process.env.APP_ROOT);
      const data = await response.json();
      if (data.organizationId) {
        url.searchParams.set("selectedOrg", data.organizationId);
        url.searchParams.set("toast", "Accepted invitation.");
      }
      const redirectPath = url.pathname + url.search;
      router.push(redirectPath);
    } else {
      const errorData: ErrorResponse = await response.json();
      const message = generateErrorMessage(
        errorData.errors,
        "Unable to log in. Please try again."
      );
      form.setError("root", { message });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-4">
          {form.formState.errors.root && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root.message}
              </AlertDescription>
            </Alert>
          )}
          <div className="grid gap-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="name@email.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid gap-2">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="password" placeholder="Password" {...field} />
                  </FormControl>
                  <FormMessage />
                  {/* @todo: forgot password */}
                  {/* <FormDescription>
                    <Link
                      href="/forgot-password"
                      className="ml-auto inline-block text-sm underline"
                    >
                      Forgot your password?
                    </Link>
                  </FormDescription> */}
                </FormItem>
              )}
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={
              form.formState.isSubmitting || form.formState.isSubmitSuccessful
            }
          >
            {(form.formState.isSubmitting ||
              form.formState.isSubmitSuccessful) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Sign in with email
          </Button>
        </div>
      </form>
    </Form>
  );
}
